import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Sparkles, Eye, CircleCheck, CircleAlert } from 'lucide-react';
import { useToast } from '@kollez/design-system';
import { apiPost } from '@/lib/api';

/** /assess/exams/new — direct port of kollez/pages/assess/builder.html. */
interface Section { name: string; source: 'fixed' | 'pool'; filter: string; count: number; minutes: number; marksEach: number; mix: [number, number, number]; minPct: number; available: number; }

const DEFAULT_SECS: Section[] = [
  { name: 'Quantitative aptitude', source: 'pool', filter: 'Quantitative · approved', count: 10, minutes: 15, marksEach: 1.5, mix: [4, 4, 2], minPct: 40, available: 212 },
  { name: 'Logical reasoning', source: 'pool', filter: 'Reasoning · approved', count: 10, minutes: 15, marksEach: 1.5, mix: [3, 5, 2], minPct: 40, available: 168 },
  { name: 'SQL and databases', source: 'pool', filter: 'SQL · approved', count: 8, minutes: 15, marksEach: 2, mix: [2, 4, 2], minPct: 0, available: 6 },
  { name: 'Coding', source: 'fixed', filter: 'Q-2045, Q-2071', count: 2, minutes: 25, marksEach: 12.5, mix: [0, 1, 1], minPct: 50, available: 2 },
  { name: 'Communication', source: 'pool', filter: 'Spoken answer · approved', count: 2, minutes: 5, marksEach: 1.5, mix: [0, 2, 0], minPct: 0, available: 101 },
];

const BASELINE_SECS: Section[] = [
  { name: 'Mathematics', source: 'pool', filter: 'Maths · 12th level', count: 15, minutes: 20, marksEach: 1, mix: [5, 7, 3], minPct: 0, available: 410 },
  { name: 'English', source: 'pool', filter: 'English · reading and grammar', count: 15, minutes: 20, marksEach: 1, mix: [5, 7, 3], minPct: 0, available: 380 },
  { name: 'Reasoning', source: 'pool', filter: 'Reasoning · approved', count: 10, minutes: 15, marksEach: 1, mix: [4, 4, 2], minPct: 0, available: 168 },
];

export function AssessBuilderPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const tpl = params.get('tpl');
  const [secs, setSecs] = useState<Section[]>(tpl === 'First-year baseline' ? BASELINE_SECS : DEFAULT_SECS);
  const [cut, setCut] = useState(55);
  const [flow, setFlow] = useState(tpl === 'First-year baseline' ? 'First-year onboarding' : 'Drive assessment');
  const [name, setName] = useState(tpl === 'First-year baseline' ? 'First-year baseline 2026' : 'Aptitude and coding screen');
  const [opensAt, setOpensAt] = useState('2026-09-22T10:00');
  const [closesAt, setClosesAt] = useState('2026-09-22T18:00');
  const [saving, setSaving] = useState(false);

  const summary = useMemo(() => {
    const questions = secs.reduce((sum, s) => sum + s.count, 0);
    const minutes = secs.reduce((sum, s) => sum + s.minutes, 0);
    const marks = secs.reduce((sum, s) => sum + s.count * s.marksEach, 0);
    return { questions, minutes, marks: Math.round(marks) };
  }, [secs]);

  const validations = useMemo(() => {
    const out: Array<{ ok: boolean; label: string }> = [];
    out.push({ ok: summary.questions >= 10, label: `At least 10 questions (have ${summary.questions})` });
    out.push({ ok: summary.minutes >= 30, label: `Duration at least 30 min (have ${summary.minutes})` });
    const shortages = secs.filter((s) => s.source === 'pool' && s.available < s.count * 3);
    out.push({ ok: shortages.length === 0, label: shortages.length === 0 ? 'Question banks have 3× more approved questions than the draw' : `${shortages[0]?.name} pool is too small (${shortages[0]?.available} vs ${(shortages[0]?.count ?? 0) * 3} needed)` });
    out.push({ ok: cut >= 40 && cut <= 90, label: 'Pass mark within a reasonable range' });
    return out;
  }, [summary, secs, cut]);

  const allOk = validations.every((v) => v.ok);

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .eb { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1180px) { .eb { grid-template-columns: minmax(0,1fr) 340px; } .eb-side { position: sticky; top: 76px; } }
        .sec-card { border: 1px solid rgb(var(--line-tertiary)); border-radius: 10px; padding: 14px; display: grid; gap: 10px; background: rgb(var(--bg-secondary)); }
        .rule-row { display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 12px; align-items: center; padding: 10px 0; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .rule-row:last-child { border-bottom: 0; }
        .sum-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .sum-grid b { display: block; font-family: "Newsreader", Georgia, serif; font-size: 1.5rem; font-weight: 500; line-height: 1.1; }
        .mix-bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; width: 120px; background: rgb(var(--bg-tertiary)); }
        .mix-bar span:nth-child(1) { background: rgb(var(--ink-accent) / .3); }
        .mix-bar span:nth-child(2) { background: rgb(var(--ink-accent) / .65); }
        .mix-bar span:nth-child(3) { background: rgb(var(--ink-accent)); }
      `}</style>

      {/* Deep-audit fix (Page 58/N): eyebrow used to fall back to
          'Wipro Elite NTH' when tpl was unset — a specific partner
          name showing on every new-exam page. Falls back to
          'Untitled draft' now. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">{flow} · {tpl ?? 'Untitled draft'}</p>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ fontFamily: '"Newsreader", Georgia, serif', fontSize: '1.75rem', fontWeight: 500, border: 'none', padding: 0, background: 'transparent' }}
            aria-label="Exam name"
          />
          <p className="subhead">Set the rules once. Nexie runs the exam as a chat, grades what it can, and sends anything uncertain to you.</p>
        </div>
        <div className="ph-actions"><span className="helper">{saving ? 'Publishing…' : 'Draft'}</span></div>
      </div>

      <div className="eb">
        <div className="grid gap-5 min-w-0">
          <section className="card card-pad grid gap-4">
            <h2 className="h-section">1. Where this exam fits</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5">
                <span className="label">Used for</span>
                <select className="select" value={flow} onChange={(e) => setFlow(e.target.value)}>
                  <option>Drive assessment</option><option>Campus screening (company)</option><option>First-year onboarding</option><option>Yearly skills check</option><option>Practice</option>
                </select>
              </label>
              {/* Deep-audit fix (Page 58/N): 'Linked to' select had three
                  hardcoded Wipro/Infosys/Deloitte options that appeared
                  on every tenant. Real linking to an opportunity ships
                  once /opportunities picker is wired here. */}
              <label className="grid gap-1.5">
                <span className="label">Linked to</span>
                <select className="select" disabled>
                  <option>Link an opportunity (coming soon)</option>
                </select>
              </label>
            </div>
          </section>

          <section className="card card-pad grid gap-4">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h2 className="h-section">2. Sections</h2>
              <span className="helper">{secs.length} sections · {summary.questions} questions · {summary.minutes} min</span>
            </div>
            <div className="grid gap-3">
              {secs.map((s, i) => (
                <div key={s.name} className="sec-card">
                  <div className="flex items-center gap-3 flex-wrap">
                    <b>{s.name}</b>
                    <span className="tag">{s.source === 'pool' ? 'Pool' : 'Fixed'}</span>
                    <span className="cell-sub">{s.filter}</span>
                    <span className="cell-sub ml-auto">{s.available} available</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-4 text-sm">
                    <label className="grid gap-1.5">
                      <span className="label">Questions</span>
                      <input className="input" type="number" value={s.count} onChange={(e) => setSecs((ss) => ss.map((x, j) => (j === i ? { ...x, count: +e.target.value } : x)))} />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="label">Minutes</span>
                      <input className="input" type="number" value={s.minutes} onChange={(e) => setSecs((ss) => ss.map((x, j) => (j === i ? { ...x, minutes: +e.target.value } : x)))} />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="label">Marks each</span>
                      <input className="input" type="number" step={0.5} value={s.marksEach} onChange={(e) => setSecs((ss) => ss.map((x, j) => (j === i ? { ...x, marksEach: +e.target.value } : x)))} />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="label">Min %</span>
                      <input className="input" type="number" value={s.minPct} onChange={(e) => setSecs((ss) => ss.map((x, j) => (j === i ? { ...x, minPct: +e.target.value } : x)))} />
                    </label>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="cell-sub">Difficulty mix</span>
                    <div className="mix-bar">
                      <span style={{ width: `${(s.mix[0] / s.count) * 100}%` }} />
                      <span style={{ width: `${(s.mix[1] / s.count) * 100}%` }} />
                      <span style={{ width: `${(s.mix[2] / s.count) * 100}%` }} />
                    </div>
                    <span className="cell-sub">{s.mix[0]} easy · {s.mix[1]} medium · {s.mix[2]} hard</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button className="btn btn-outline btn-sm" onClick={() => setSecs((ss) => [...ss, { name: 'New section', source: 'pool', filter: 'Any · approved', count: 5, minutes: 10, marksEach: 1, mix: [2, 2, 1], minPct: 0, available: 100 }])}>
                <Plus size={14} />Add section
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled
                title="Wires to /nexie/parse-jd → autofill sections in a follow-up. Add sections manually for now."
              >
                <Sparkles size={14} />Suggest sections · soon
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="check" defaultChecked />
              Every candidate gets questions of the same level mix, drawn separately (no two candidates see the same paper)
            </label>
          </section>

          <section className="card card-pad grid gap-3">
            <h2 className="h-section">3. Scoring and gates</h2>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Wrong answers</div><div className="cell-sub">Discourages guessing on choice questions</div></div>
              <select className="select" style={{ width: 'auto' }}><option>Minus ¼ of the marks</option><option>No penalty</option><option>Minus ½ of the marks</option></select>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Pass mark</div><div className="cell-sub">Overall, out of {summary.marks}</div></div>
              <div className="flex items-center gap-2">
                <input className="input" type="number" value={cut} onChange={(e) => setCut(+e.target.value)} style={{ width: 80 }} />
                <span className="cell-sub">%</span>
              </div>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Section minimums</div><div className="cell-sub">Fail if any section is below its minimum</div></div>
              <span className="switch"><input type="checkbox" defaultChecked /><span /></span>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Integrity hold</div><div className="cell-sub">Below this integrity score, results wait for a person — never auto-rejected</div></div>
              <div className="flex items-center gap-2"><input className="input" type="number" defaultValue={70} style={{ width: 80 }} /><span className="cell-sub">/ 100</span></div>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Nexie's grading</div><div className="cell-sub">Written, spoken and code answers. Less confident than this → reviewer.</div></div>
              <div className="flex items-center gap-2"><input className="input" type="number" defaultValue={80} style={{ width: 80 }} /><span className="cell-sub">%</span></div>
            </div>
          </section>

          <section className="card card-pad grid gap-3">
            <h2 className="h-section">4. How Nexie runs it</h2>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Clarify wording</div><div className="cell-sub">Nexie can rephrase a question or explain a term. It never hints at the answer.</div></div>
              <span className="switch"><input type="checkbox" defaultChecked /><span /></span>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Follow-up questions</div><div className="cell-sub">For written answers, Nexie may ask one "why?" before scoring</div></div>
              <span className="switch"><input type="checkbox" defaultChecked /><span /></span>
            </div>
            <div className="rule-row">
              <div><div className="font-medium text-sm">Adapt difficulty to the candidate</div><div className="cell-sub">Off for hiring; good for practice and baselines</div></div>
              <span className="switch"><input type="checkbox" /><span /></span>
            </div>
            <p className="eyebrow m-0">Invigilation</p>
            <div className="flex flex-wrap gap-2">
              {['Camera on', 'Photo ID check', 'Full screen', 'Warn on tab switch', 'Block paste', 'Record screen', 'Microphone', 'One device'].map((t, i) => (
                <button key={t} className="tag" aria-pressed={i !== 5 && i !== 6}>{t}</button>
              ))}
            </div>
            <p className="helper m-0">Candidates see exactly what is monitored before they start, and must agree. Recordings are deleted after 90 days.</p>
          </section>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section">5. Who takes it, and when</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5"><span className="label">Candidates</span><select className="select"><option>Everyone shortlisted in the drive (64)</option><option>Everyone who applied (412)</option><option>Pick students…</option><option>Upload a list</option></select></label>
              <label className="grid gap-1.5"><span className="label">Attempts</span><select className="select"><option>1</option><option>2 (best counts)</option></select></label>
              <label className="grid gap-1.5"><span className="label">Opens</span><input className="input" type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} /></label>
              <label className="grid gap-1.5"><span className="label">Closes</span><input className="input" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} /></label>
            </div>
            <div className="grid gap-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" defaultChecked /> Remind on WhatsApp 1 day and 1 hour before</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" defaultChecked /> 25% extra time for students with registered accommodations</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" /> Let candidates see their score right after submitting</label>
            </div>
          </section>
        </div>

        <aside className="eb-side grid gap-4">
          <div className="card card-pad grid gap-4">
            <div className="sum-grid">
              <div><span className="helper">Questions</span><b>{summary.questions}</b></div>
              <div><span className="helper">Duration</span><b>{summary.minutes} min</b></div>
              <div><span className="helper">Marks</span><b>{summary.marks}</b></div>
              <div><span className="helper">Candidates</span><b>64</b></div>
            </div>
          </div>
          <div className="card card-pad grid gap-2">
            <h2 className="h-section">Ready to publish?</h2>
            <div className="grid gap-2">
              {validations.map((v) => (
                <div key={v.label} className={`qcheck ${v.ok ? 'ok' : 'warn'}`}>
                  {v.ok ? <CircleCheck size={14} /> : <CircleAlert size={14} />}
                  <span>{v.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card card-pad grid gap-2">
            <Link className="btn btn-outline" to="/assess/take/preview"><Eye size={14} />Try it as a candidate</Link>
            <button
              className="btn btn-primary"
              disabled={!allOk || saving}
              onClick={async () => {
                if (!allOk) {
                  toast.show({ tone: 'warning', message: 'Fix the checks first.' });
                  return;
                }
                setSaving(true);
                try {
                  const opens = new Date(opensAt);
                  const closes = new Date(closesAt);
                  if (isNaN(opens.getTime()) || isNaN(closes.getTime()) || closes <= opens) {
                    throw new Error('Set a valid opens/closes window');
                  }
                  const body = {
                    name,
                    description: `${flow} · ${tpl ?? 'exam'}`,
                    sections: secs.map((s) => ({
                      title: s.name,
                      source: s.source,
                      timeLimitMin: s.minutes,
                    })),
                    scoring: {
                      totalMarks: summary.marks,
                      passingMarks: Math.round((summary.marks * cut) / 100),
                      negativeMarking: false,
                    },
                    gates: { faceMatch: false, photoIdCheck: false, minTimePerQuestionSec: 0 },
                    delivery: { mode: 'unproctored' as const, randomizeQuestions: true, showResultsImmediately: false, allowAttempts: 1 },
                    window: { opensAt: opens.toISOString(), closesAt: closes.toISOString() },
                  };
                  await apiPost<{ assessment: unknown }>('/assessments/exams', body);
                  toast.show({ tone: 'success', message: 'Saved as draft. Mark it as ready to attach from the exam list once questions are attached.' });
                  nav('/assess/exams');
                } catch (err) {
                  const msg = err instanceof Error ? err.message : 'Could not save exam';
                  toast.show({ tone: 'warning', message: msg });
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? 'Saving…' : 'Save exam'}
            </button>
            <p className="helper m-0 text-center">{allOk ? 'Ready to save as draft. Marking ready-to-attach needs approved questions per section.' : 'Some checks are still failing.'}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
